import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { BIDDING_STRATEGIES, type AdGroupFormData } from '@/types/adGroup';
import type { DisplayAdFormData } from './DisplayAdCreationStep';
import type { DemandGenAdFormData } from './DemandGenAdCreationStep';

interface AdGroupReviewStepProps {
  formData: AdGroupFormData;
  campaignName: string;
  campaignType?: string;
  adType?: 'RSA' | 'RDA' | 'SHOPPING' | 'DEMAND_GEN';
  displayFormData?: DisplayAdFormData;
  demandGenFormData?: DemandGenAdFormData;
}

export const AdGroupReviewStep: React.FC<AdGroupReviewStepProps> = ({
  formData,
  campaignName,
  campaignType,
  adType = 'RSA',
  displayFormData,
  demandGenFormData,
}) => {
  const selectedStrategy = BIDDING_STRATEGIES.find((s) => s.value === formData.bidding_strategy);

  const formatCurrency = (value: string | undefined) => {
    if (!value || value === '') return 'Not set';
    const num = parseFloat(value);
    return isNaN(num) ? 'Invalid' : `$${num.toFixed(2)}`;
  };

  const getAdTypeLabel = () => {
    switch (adType) {
      case 'RDA':
        return 'Responsive Display Ad';
      case 'SHOPPING':
        return 'Shopping Product Ads';
      case 'DEMAND_GEN':
        return getDemandGenAdTypeLabel();
      default:
        return 'Responsive Search Ad';
    }
  };

  const getDemandGenAdTypeLabel = () => {
    if (!demandGenFormData) return 'Demand Gen Ad';
    switch (demandGenFormData.adType) {
      case 'MULTI_ASSET':
        return 'Demand Gen Multi-Asset Ad';
      case 'VIDEO':
        return 'Demand Gen Video Ad';
      case 'CAROUSEL':
        return 'Demand Gen Carousel Ad';
      default:
        return 'Demand Gen Ad';
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Review & Create</h3>
        <p className="text-sm text-gray-600">
          Review your ad group settings before creating.
        </p>
      </div>

      {/* Summary Card */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {/* Campaign */}
        <div className="p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-1">Campaign</h4>
          <p className="text-base text-gray-900">{campaignName}</p>
          {campaignType && (
            <p className="text-xs text-gray-500 mt-1">{campaignType.replace(/_/g, ' ')}</p>
          )}
        </div>

        {/* Basic Info */}
        <div className="p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h4>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Ad Group Name:</span>
            <span className="text-sm font-medium text-gray-900">{formData.name}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Status:</span>
            <Badge variant={formData.status === 'ENABLED' ? 'success' : 'warning'}>
              {formData.status.charAt(0).toUpperCase() + formData.status.slice(1).toLowerCase()}
            </Badge>
          </div>
        </div>

        {/* Bidding Strategy */}
        <div className="p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Bidding Strategy</h4>

          <div className="flex justify-between items-start">
            <span className="text-sm text-gray-600">Strategy:</span>
            <div className="text-right">
              <span className="text-sm font-medium text-gray-900">
                {selectedStrategy?.label}
              </span>
              <p className="text-xs text-gray-500 mt-1">
                {selectedStrategy?.description}
              </p>
            </div>
          </div>

          {selectedStrategy?.requiresInput && (
            <>
              {selectedStrategy.inputType === 'cpc' && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Max CPC Bid:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(formData.cpc_bid_dollars)}
                  </span>
                </div>
              )}

              {selectedStrategy.inputType === 'cpa' && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Target CPA:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(formData.target_cpa_dollars)}
                  </span>
                </div>
              )}

              {selectedStrategy.inputType === 'roas' && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Target ROAS:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formData.target_roas ? `${formData.target_roas}×` : 'Not set'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Ad Information - RSA */}
        {adType === 'RSA' && (
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">{getAdTypeLabel()}</h4>

            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-600 font-medium">Final URL:</span>
                <p className="text-sm text-gray-900 break-all">{formData.finalUrl}</p>
              </div>

              {(formData.path1 || formData.path2) && (
                <div>
                  <span className="text-xs text-gray-600 font-medium">Display Paths:</span>
                  <p className="text-sm text-gray-900">
                    {[formData.path1, formData.path2].filter(Boolean).join(' / ')}
                  </p>
                </div>
              )}

              <div>
                <span className="text-xs text-gray-600 font-medium">
                  Headlines ({formData.headlines.filter((h) => h.trim()).length}):
                </span>
                <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                  {formData.headlines
                    .filter((h) => h.trim())
                    .map((headline, i) => (
                      <li key={i} className="truncate">
                        {headline}
                      </li>
                    ))}
                </ul>
              </div>

              <div>
                <span className="text-xs text-gray-600 font-medium">
                  Descriptions ({formData.descriptions.filter((d) => d.trim()).length}):
                </span>
                <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                  {formData.descriptions
                    .filter((d) => d.trim())
                    .map((description, i) => (
                      <li key={i} className="text-sm">
                        {description}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Ad Information - RDA */}
        {adType === 'RDA' && displayFormData && (
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">{getAdTypeLabel()}</h4>

            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-600 font-medium">Business Name:</span>
                <p className="text-sm text-gray-900">{displayFormData.businessName}</p>
              </div>

              <div>
                <span className="text-xs text-gray-600 font-medium">Final URL:</span>
                <p className="text-sm text-gray-900 break-all">{displayFormData.finalUrl}</p>
              </div>

              <div>
                <span className="text-xs text-gray-600 font-medium">Images:</span>
                <div className="text-sm text-gray-900 mt-1">
                  <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                    {displayFormData.landscapePending.length} landscape
                  </span>
                  <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                    {displayFormData.squarePending.length} square
                  </span>
                  {displayFormData.logoSquarePending.length > 0 && (
                    <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                      {displayFormData.logoSquarePending.length} logo
                    </span>
                  )}
                </div>
              </div>

              <div>
                <span className="text-xs text-gray-600 font-medium">
                  Short Headlines ({displayFormData.shortHeadlines.filter((h) => h.trim()).length}):
                </span>
                <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                  {displayFormData.shortHeadlines
                    .filter((h) => h.trim())
                    .map((headline, i) => (
                      <li key={i} className="truncate">
                        {headline}
                      </li>
                    ))}
                </ul>
              </div>

              <div>
                <span className="text-xs text-gray-600 font-medium">Long Headline:</span>
                <p className="text-sm text-gray-900">{displayFormData.longHeadline}</p>
              </div>

              <div>
                <span className="text-xs text-gray-600 font-medium">
                  Descriptions ({displayFormData.descriptions.filter((d) => d.trim()).length}):
                </span>
                <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                  {displayFormData.descriptions
                    .filter((d) => d.trim())
                    .map((description, i) => (
                      <li key={i} className="text-sm">
                        {description}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Ad Information - Shopping */}
        {adType === 'SHOPPING' && (
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">{getAdTypeLabel()}</h4>
            <p className="text-sm text-gray-600">
              Ads will be automatically generated from your Google Merchant Center product feed.
            </p>
          </div>
        )}

        {/* Ad Information - Demand Gen */}
        {adType === 'DEMAND_GEN' && demandGenFormData && (
          <div className="p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">{getAdTypeLabel()}</h4>

            <div className="space-y-2">
              {/* Business Name */}
              <div>
                <span className="text-xs text-gray-600 font-medium">Business Name:</span>
                <p className="text-sm text-gray-900">{demandGenFormData.businessName}</p>
              </div>

              {/* Call to Action */}
              <div>
                <span className="text-xs text-gray-600 font-medium">Call to Action:</span>
                <p className="text-sm text-gray-900">
                  {demandGenFormData.callToAction.replace(/_/g, ' ')}
                </p>
              </div>

              {/* Final URL - for Multi-Asset and Video only */}
              {demandGenFormData.adType !== 'CAROUSEL' && (
                <div>
                  <span className="text-xs text-gray-600 font-medium">Final URL:</span>
                  <p className="text-sm text-gray-900 break-all">{demandGenFormData.finalUrl}</p>
                </div>
              )}

              {/* Multi-Asset specific fields */}
              {demandGenFormData.adType === 'MULTI_ASSET' && (
                <>
                  <div>
                    <span className="text-xs text-gray-600 font-medium">Images:</span>
                    <div className="text-sm text-gray-900 mt-1">
                      <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                        {demandGenFormData.landscapePending.length} landscape
                      </span>
                      <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                        {demandGenFormData.squarePending.length} square
                      </span>
                      {demandGenFormData.portraitPending.length > 0 && (
                        <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                          {demandGenFormData.portraitPending.length} portrait
                        </span>
                      )}
                      <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2 mb-1">
                        {demandGenFormData.logoPending.length} logo
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 font-medium">
                      Headlines ({demandGenFormData.headlines.filter((h) => h.trim()).length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                      {demandGenFormData.headlines
                        .filter((h) => h.trim())
                        .map((headline, i) => (
                          <li key={i} className="truncate">
                            {headline}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 font-medium">
                      Descriptions ({demandGenFormData.descriptions.filter((d) => d.trim()).length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                      {demandGenFormData.descriptions
                        .filter((d) => d.trim())
                        .map((description, i) => (
                          <li key={i} className="text-sm">
                            {description}
                          </li>
                        ))}
                    </ul>
                  </div>
                </>
              )}

              {/* Video specific fields */}
              {demandGenFormData.adType === 'VIDEO' && (
                <>
                  <div>
                    <span className="text-xs text-gray-600 font-medium">
                      YouTube Videos ({demandGenFormData.videoIds.filter((v) => v.trim()).length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                      {demandGenFormData.videoIds
                        .filter((v) => v.trim())
                        .map((videoId, i) => (
                          <li key={i} className="truncate">
                            {videoId}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 font-medium">Logo Images:</span>
                    <span className="inline-block bg-gray-100 px-2 py-1 rounded ml-2">
                      {demandGenFormData.logoPending.length} logo(s)
                    </span>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 font-medium">
                      Headlines ({demandGenFormData.headlines.filter((h) => h.trim()).length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                      {demandGenFormData.headlines
                        .filter((h) => h.trim())
                        .map((headline, i) => (
                          <li key={i} className="truncate">
                            {headline}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 font-medium">
                      Long Headlines ({demandGenFormData.longHeadlines.filter((h) => h.trim()).length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                      {demandGenFormData.longHeadlines
                        .filter((h) => h.trim())
                        .map((headline, i) => (
                          <li key={i} className="truncate">
                            {headline}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 font-medium">
                      Descriptions ({demandGenFormData.descriptions.filter((d) => d.trim()).length}):
                    </span>
                    <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
                      {demandGenFormData.descriptions
                        .filter((d) => d.trim())
                        .map((description, i) => (
                          <li key={i} className="text-sm">
                            {description}
                          </li>
                        ))}
                    </ul>
                  </div>
                </>
              )}

              {/* Carousel specific fields */}
              {demandGenFormData.adType === 'CAROUSEL' && (
                <div>
                  <span className="text-xs text-gray-600 font-medium">
                    Carousel Cards ({demandGenFormData.carouselCards.length}):
                  </span>
                  <div className="space-y-2 mt-2">
                    {demandGenFormData.carouselCards.map((card, i) => (
                      <div key={i} className="bg-gray-50 rounded p-2 text-sm">
                        <div className="font-medium text-gray-900">Card {i + 1}</div>
                        <div className="text-gray-600 truncate">
                          Headline: {card.headline || '(empty)'}
                        </div>
                        <div className="text-gray-600 truncate text-xs">
                          URL: {card.finalUrl || '(empty)'}
                        </div>
                        <div className="text-gray-500 text-xs">
                          Image: {card.pending ? "Selected" : "Not selected"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">Next Steps</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>After creating this ad group, scroll down to the <span className="font-medium">Campaign Management</span> card where you can:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                {adType === 'RSA' && <li>Add keywords to target relevant searches</li>}
                {adType === 'RDA' && <li>Configure targeting options for your display ads</li>}
                {adType === 'SHOPPING' && <li>Ensure your Merchant Center feed is up to date</li>}
                {adType === 'DEMAND_GEN' && <li>Adjust channel controls and view your ads</li>}
                <li>Monitor performance and optimize</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Additional ads info message */}
      {adType !== 'SHOPPING' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm text-blue-800">
                <span className="font-medium">Want to create additional ads?</span>{' '}
                You can add more ads after creating the ad group through the{' '}
                <span className="font-medium">Create New Ad</span> button on the campaign's page.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
